/** ENG-AI-UI-RENDER-1 — the AI-narrative panel.
 *
 * Extracted from Normalization.tsx, which is 1538 lines and past the point
 * where FILE_SIZE_POLICY wants a split rather than another edit in place.
 *
 * What this panel is responsible for, and why each part is here:
 *
 *  * **Showing the saved draft the moment it exists.** Generation stores a
 *    draft server-side; the panel must reflect that with no page reload. It
 *    does so by merging the typed patch from `@/api/narrative` — never the raw
 *    POST body, whose field names differ from the session's (see that module).
 *  * **Never billing twice for one draft.** Once a draft is on screen the
 *    Generate control is gone, replaced by Approve/Discard. An in-flight ref
 *    closes the remaining window (a second click landing before React has
 *    re-rendered the disabled button), and `generate` refuses outright if a
 *    draft is already held. Approve and Discard are free; only generate bills.
 *  * **Keeping the two gates apart.** The badge reads `narrative_status` — the
 *    PROSE gate. The statement gate (`status` / `approved_by`) lives in the
 *    parent and is never written from here; the patch type makes that
 *    impossible to get wrong by accident.
 */
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatApiError } from '@/utils/httpErrors'
import {
  approveDealNarrative, canDraftNarrative, canReleaseNarrative,
  discardDealNarrative, generateDealNarrative,
  type NarrativeFields,
} from '@/api/narrative'

interface AiNarrativePanelProps {
  dealId: string | null
  /** The narrative slice of the loaded session. Owned by the parent so a deal
   *  switch or a fresh GET resets it in one place. */
  narrative: NarrativeFields
  /** The caller's role, straight off the JWT claims. The panel derives both
   *  capabilities from it with the same lists the router enforces, rather than
   *  taking a caller-computed flag that could drift from the endpoint. */
  role: string | null | undefined
  /** Flush the parent's debounced auto-save before generating. The narrative is
   *  written from the SAVED figures, so an unsaved keystroke would produce
   *  prose about numbers the database has never seen. */
  onFlush?: () => Promise<void>
  /** Merge a narrative-only patch into the parent's state. */
  onChange: (patch: Partial<NarrativeFields>) => void
}

export function AiNarrativePanel({
  dealId, narrative, role, onFlush, onChange,
}: AiNarrativePanelProps) {
  const canDraft = canDraftNarrative(role)
  const canRelease = canReleaseNarrative(role)
  // One flag for all three actions — they are mutually exclusive on the same
  // draft. The ref is the one that actually prevents a double-spend: state
  // updates are async, so two clicks in the same tick both see `busy === false`.
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)

  const run = useCallback(async (fn: () => Promise<void>) => {
    if (!dealId || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    try {
      await fn()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }, [dealId])

  const generate = useCallback(() => run(async () => {
    // Belt and braces with the render branch below: a draft already exists,
    // so there is nothing to pay for. Discard first to replace it.
    if (narrative.ai_narrative) return
    if (onFlush) await onFlush()
    const result = await generateDealNarrative(dealId as string)
    if (result.outcome === 'unavailable') {
      // 200, not an error: capacity. Nothing was stored, so the panel stays
      // exactly as it was and the Generate button remains available.
      toast.message(result.message)
      return
    }
    onChange(result.fields)
    if (result.guardPassed) {
      toast.success('Draft ready for your review.')
    } else {
      toast.warning('Draft ready, but some figures do not match. Review before approving.')
    }
  }), [run, dealId, narrative.ai_narrative, onFlush, onChange])

  const approve = useCallback(() => run(async () => {
    onChange(await approveDealNarrative(dealId as string))
    toast.success('Narrative approved — it will appear in the report.')
  }), [run, dealId, onChange])

  const discard = useCallback(() => run(async () => {
    onChange(await discardDealNarrative(dealId as string))
    toast.success('Draft discarded.')
  }), [run, dealId, onChange])

  const guard = narrative.narrative_guard
  const flags = guard?.flags ?? []

  // A role the router would 403 on every narrative action, with nothing stored
  // to read: offering the section at all would only be a dead end. An existing
  // draft still renders below — reading is not one of the gated actions.
  if (!canDraft && !narrative.ai_narrative) return null

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-brand-text">AI Narrative</p>

      {/* The badge reads narrative_status, NOT `approved_by` — that field is
          the STATEMENT sign-off, and using it here told the CPA their narrative
          was approved the moment they signed off the figures, which is a
          different decision entirely.

          Approve is offered only when the guard passed. The server enforces
          that too (409 with the flags); this only saves the CPA a round trip
          to be told no. */}
      {narrative.ai_narrative ? (
        <div className="space-y-2" data-testid="narrative-draft">
          {narrative.narrative_status === 'approved' ? (
            <Badge className="bg-emerald-100 text-emerald-800 text-[10px] hover:bg-emerald-100">
              Approved{narrative.narrative_approved_by
                ? ` by ${narrative.narrative_approved_by}` : ''}
            </Badge>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-amber-100 text-amber-800 text-[10px] hover:bg-amber-100">
                Pending CPA Approval
              </Badge>
              {/* Shown to anyone who can work the draft, enabled only for the
                  roles POST approve-narrative accepts. An analyst sees it
                  disabled rather than absent: they need to know the draft is
                  waiting on a CPA, not that approval does not exist. It can
                  never be clicked into a 403. */}
              {canDraft && (
                <Button
                  size="sm" variant="outline" className="h-6 text-[10px] px-2"
                  data-testid="approve-narrative"
                  disabled={busy || !canRelease || !guard?.passed}
                  title={!canRelease
                    ? 'Only the engagement CPA can approve a narrative'
                    : guard?.passed
                      ? 'Release this narrative into the report'
                      : 'Resolve the flagged figures first'}
                  onClick={approve}
                >
                  Approve
                </Button>
              )}
              {canDraft && (
                <Button
                  size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                  data-testid="discard-narrative"
                  disabled={busy}
                  onClick={discard}
                >
                  Discard
                </Button>
              )}
            </div>
          )}

          {narrative.narrative_status !== 'approved' && canDraft && !canRelease && (
            <p className="text-[10px] text-muted-foreground" data-testid="narrative-awaiting-cpa">
              Only the engagement CPA can approve this. Until they do, it stays
              out of the report and out of the seller and buyer portals.
            </p>
          )}

          {/* The whole point of the guard: the CPA sees exactly which figures
              are disputed, in the same words the server refused on, before
              deciding. Never collapsed or truncated. */}
          {guard && !guard.passed && flags.length > 0 && (
            <div
              className="rounded border border-amber-300 bg-amber-50 p-2 space-y-1"
              data-testid="narrative-guard-flags"
            >
              <p className="text-[10px] font-medium text-amber-900">
                {flags.length} figure{flags.length === 1 ? '' : 's'} could
                not be matched to this deal&apos;s computed numbers:
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                {flags.map((f, i) => (
                  <li key={i} className="text-[10px] text-amber-900 leading-relaxed">
                    {f.message}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-amber-900/80">
                Regenerate, or edit the narrative in the report, before approving.
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
            {narrative.ai_narrative}
          </p>
          <p className="text-[10px] text-muted-foreground">
            AI-drafted from this deal&apos;s computed figures. It reaches the
            report only after you approve it.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs gap-1.5"
            data-testid="generate-narrative"
            disabled={busy || !dealId}
            title="Draft the Revenue, Expense and Balance Sheet sections from this deal's computed figures"
            onClick={generate}
          >
            {busy ? 'Drafting…' : 'Draft AI narrative'}
          </Button>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Drafts §4–§6 from the figures above. Every number is checked
            against them, and nothing reaches the report until you approve it.
          </p>
        </div>
      )}
    </div>
  )
}
